import { useRef } from "react";
import { RENDER_DEBUG } from "../debug-flags";

/**
 * Log which prop keys changed identity between renders of a component.
 *
 * Usage:
 *   useRenderDiff("ChannelItem", {
 *     channel,
 *     isSelected,
 *     onSelect,
 *     onEdit,
 *     ...
 *   });
 *
 * When RENDER_DEBUG is on, logs one of:
 *   [ChannelItem:diff] changed: onSelect, onEdit
 *   [ChannelItem:diff] no prop changes (rendered anyway — parent re-rendered)
 *
 * Compares by Object.is (reference for objects/functions, value for primitives).
 * No-op when RENDER_DEBUG is off.
 */
/* eslint-disable react-hooks/refs */
export function useRenderDiff(
  name: string,
  values: Record<string, unknown>,
): void {
  const prevRef = useRef<Record<string, unknown> | null>(null);
  if (!RENDER_DEBUG) return;

  const prev = prevRef.current;

  if (prev) {
    const changed: string[] = [];
    for (const key of Object.keys(values)) {
      if (!Object.is(prev[key], values[key])) changed.push(key);
    }
    for (const key of Object.keys(prev)) {
      if (!(key in values)) changed.push(`-${key}`);
    }

    if (changed.length > 0) {
      console.debug(`[${name}:diff] changed:`, changed.join(", "));
    } else {
      console.debug(
        `[${name}:diff] no prop changes (rendered anyway — parent re-rendered)`,
      );
    }
  }

  prevRef.current = { ...values };
}
/* eslint-enable react-hooks/refs */
